import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  HelpCircle,
  Activity,
  Network,
  Laptop,
  Building2,
  Siren,
  Users,
  ShoppingCart,
  LineChart,
  FlaskConical,
  Cable,
  MonitorCog,
  X,
  LayoutDashboard,
  User,
  Grid2x2Check,
  ChartBar,
  DollarSign,
  KeyboardIcon,
  Rocket,
} from 'lucide-react';
import { AlertSearch } from './AlertSearch/AlertSearch';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Roles, useRouteAccess } from '@/app/Roles/useRouteAccess';

import { useNavigationStore, navStore } from '@/store/navigationStore';

import React, { useEffect } from 'react';
import { FeatureFlags } from '@/lib/feature-flag';
import ShiftManager from '@/app/Shift/ShiftManager';
import { Sheet, SheetContent, SheetOverlay, SheetPortal } from '@/components/ui/sheet';
import { RoutePath } from '@/lib/routes';
import { useAuthStore } from '@/store/authStore';
import { restartOnboardingForCurrentRoute, hasOnboardingForCurrentRoute } from '@/lib/onboardingTours/driver';
import { HELP_CENTER_LINKS } from '@/lib/onboardingTours/helpCenterLinks';

interface NavigationItem {
  label: React.ReactNode;
  href: string;
  icon: JSX.Element;
  description?: string;
  route?: string;
  requireFlag?: boolean;
  flag?: FeatureFlags;
}

interface NavigationSection {
  title: string;
  requireFlag?: boolean;
  flag?: FeatureFlags;
  items: NavigationItem[];
  sectionIcon?: JSX.Element;
}

interface NavigationItemComponentProps {
  item: NavigationItem;
  isActive: boolean;
  onClick: () => void;
  sectionKey: string;
}

// Color mapping for each section
const sectionColors = {
  monitoring: {
    bg: 'bg-emerald-500/20',
    hoverBg: 'group-hover:bg-emerald-500/30',
    text: 'text-emerald-400',
    hoverText: 'group-hover:text-emerald-300'
  },
  watchfulOS: {
    bg: 'bg-blue-500/20',
    hoverBg: 'group-hover:bg-blue-500/30',
    text: 'text-blue-400',
    hoverText: 'group-hover:text-blue-300'
  },
  business: {
    bg: 'bg-green-500/20',
    hoverBg: 'group-hover:bg-green-500/30',
    text: 'text-green-400',
    hoverText: 'group-hover:text-green-300'
  },
  admin: {
    bg: 'bg-purple-500/20',
    hoverBg: 'group-hover:bg-purple-500/30',
    text: 'text-purple-400',
    hoverText: 'group-hover:text-purple-300'
  },
  internal: {
    bg: 'bg-yellow-500/20',
    hoverBg: 'group-hover:bg-yellow-500/30',
    text: 'text-yellow-400',
    hoverText: 'group-hover:text-yellow-300'
  }
} as const;

function NavigationItemComponent({ item, isActive, onClick, sectionKey }: NavigationItemComponentProps) {
  const isExternalLink = item.href.startsWith('http');
  const colors = sectionColors[sectionKey as keyof typeof sectionColors] || sectionColors.monitoring;
  
  const baseClasses = "group relative flex h-11 w-full items-center gap-3 overflow-hidden px-3 py-4 font-semibold text-lime/50 transition-all duration-150 ease-out hover:bg-white/5 group-hover/section:opacity-80 hover:!opacity-100";
  
  const content = (
    <>
      <div className={`flex h-8 w-8 items-center justify-center rounded-lg border border-white/5 opacity-80 transition-all duration-200 ease-out group-hover:scale-110 group-hover:opacity-100 ${colors.bg} ${colors.hoverBg} ${colors.text} ${colors.hoverText}`}>
        {item.icon}
      </div>
      <div className="relative min-w-0 flex-1">
        <div
          className={`transition-transform duration-200 ease-out text-sm ${
            isActive
              ? '-translate-y-1.5 text-lime'
              : 'text-white group-hover:-translate-y-1.5'
          }`}
        >
          {item.label}
        </div>
        {item.description && (
          <div
            className={`absolute left-0 right-0 top-3 text-xs font-normal transition-all duration-200 ease-out ${
              isActive
                ? 'text-lime/70 opacity-100'
                : 'text-white/60 opacity-0 group-hover:opacity-100'
            }`}
          >
            {item.description}
          </div>
        )}
      </div>
    </>
  );

  if (isExternalLink) {
    return (
      <a
        key={item.href}
        href={item.href}
        className={baseClasses}
        target="_blank"
        rel="noopener noreferrer"
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      key={item.href}
      to={item.href}
      className={baseClasses}
      onClick={onClick}
    >
      {content}
    </Link>
  );
}

const navigationItems: Record<string, NavigationSection> = {
  monitoring: {
    title: 'Monitoring',
    sectionIcon: <Activity className="h-3 w-3 text-red-400" />,
    items: [
      {
        label: 'Monitoring Portal',
        href: '/monitor',
        icon: <Activity className="h-4 w-4" />,
        description: 'Real-time security alerts and monitoring',
        route: '/monitor',
      },
      {
        label: 'Monitoring Stats',
        href: '/monitoring-overview',
        icon: <LayoutDashboard className="h-4 w-4" />,
        description: 'System performance and analytics',
        route: '/monitoring-overview',
        flag: FeatureFlags.MonitoringOverview,
      },
      {
        label: 'Alert Log',
        href: '/alerts',
        icon: <Siren className="h-4 w-4" />,
        description: 'Historical alert records and analysis',
        route: '/alerts',
      },
      {
        label: 'Quill Audit',
        href: '/alert-audit',
        route: '/alert-audit',
        icon: <Grid2x2Check className="h-4 w-4" />,
        description: 'Alert quality and accuracy review',
        flag: FeatureFlags.QuillAuditTool,
      },
    ],
  },
  watchfulOS: {
    title: 'System',
    requireFlag: true,
    sectionIcon: <Network className="h-3 w-3 text-blue-400" />,
    items: [
      {
        label: 'System Manager',
        href: '/viewer',
        icon: <Network className="h-4 w-4" />,
        description: 'Advanced system configuration and control',
        route: '/viewer',
      },
      {
        label: 'Customers',
        href: '/customers',
        icon: <User className="h-4 w-4" />,
        description: 'Manage customer accounts and profiles',
        route: '/customers',
      },
      {
        label: 'Sites',
        href: '/sites',
        icon: <Building2 className="h-4 w-4" />,
        description: 'Configure and monitor site locations',
        route: '/sites',
      },

      {
        label: 'Devices',
        href: '/devices',
        icon: <Laptop className="h-4 w-4" />,
        description: 'Hardware management and status',
        route: '/devices',
      },
      {
        label: 'Connections',
        href: '/connections',
        icon: <Cable className="h-4 w-4" />,
        description: 'Third-party service connections',
        route: '/connections',
      },
      {
        label: 'EdgeLink',
        href: '/edgelink',
        icon: <Network className="h-4 w-4" />,
        description: 'Network bridges for site devices',
        route: '/edgelink',
        flag: FeatureFlags.EdgeLink,
      },
      {
        label: "Reporting",
        href: RoutePath.Reporting(),
        icon: <ChartBar className="h-4 w-4" />,
        description: 'Generate reports and analytics',
        route: RoutePath.Reporting(),
      }
    ],
  },
  business: {
    title: 'Business',
    requireFlag: true,
    flag: FeatureFlags.BillingPage,
    sectionIcon: <ShoppingCart className="h-3 w-3 text-green-400" />,
    items: [
      {
        label: 'Usage & Billing',
        href: '/billing',
        icon: <DollarSign className="h-4 w-4" />,
        description: 'Monitor usage and billing metrics',
        route: '/billing',
        flag: FeatureFlags.BillingPage,
      },
    ],
  },
  admin: {
    title: 'Admin',
    requireFlag: true,
    sectionIcon: <Users className="h-3 w-3 text-purple-400" />,
    items: [
      {
        label: 'Users',
        href: '/users',
        route: '/users',
        icon: <User className="h-4 w-4" />,
        description: 'User accounts and permissions',
      },
      {
        label: ' Teams',
        href: '/team',
        route: '/team',
        icon: <Users className="h-4 w-4" />,
        description: 'Team management and collaboration',
      },
    ],
  },
  internal: {
    title: 'Watchful Internal',
    requireFlag: true,
    sectionIcon: <FlaskConical className="h-3 w-3 text-yellow-400" />,
    items: [
      {
        label: 'Integration Hub',
        href: '/ihub',
        icon: <Network className="h-4 w-4" />,
        description: 'API integrations and webhooks',
        route: '/ihub',
        flag: FeatureFlags.WatchfulOnly,
      },
      {
        label: 'Ingestion',
        href: '/ingestion',
        route: '/ingestion',
        icon: <MonitorCog className="h-4 w-4" />,
        description: 'Event pipeline and processing',
        flag: FeatureFlags.WatchfulOnly,
      },
      {
        label: <span>Inbound Webhooks <span className="ml-1 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-normal text-white/40">Legacy</span></span>,
        href: '/ingestion/legacy',
        route: '/ingestion/legacy',
        icon: <MonitorCog className="h-4 w-4" />,
        description: 'Raw webhook ingestion viewer',
        flag: FeatureFlags.WatchfulOnly,
      },

      {
        label: 'WAT Lab',
        href: 'https://lab.watchful.co.nz',
        icon: <FlaskConical className="h-4 w-4" />,
        description: 'Experimental features and testing',
        flag: FeatureFlags.WatchfulOnly,
      },
        {
        label: 'Prompt Tester',
        href: '/testing',
        icon: <KeyboardIcon className="h-4 w-4" />,
        description: 'Internal prompt testing',
        flag: FeatureFlags.WatchfulOnly,
      },
      {
        label: 'EdgeLink Deployments',
        href: '/edgelink-deployments',
        icon: <Rocket className="h-4 w-4" />,
        description: 'Manage EdgeLink package versions',
        route: '/edgelink-deployments',
        flag: FeatureFlags.WatchfulOnly,
      },
      {
        label: 'Product Analytics',
        href: 'https://us.posthog.com/project/107731/',
        icon: <LineChart className="h-4 w-4" />,
        description: 'Usage metrics and insights',
        flag: FeatureFlags.WatchfulOnly,
      },
    ],
  },
};

export function NavigationSheet() {
  const { hasAccessToRoute, hasRoleAccess } = useRouteAccess();
  const location = useLocation();
  const isNavigationOpen = useNavigationStore((state) => state.isNavigationOpen);
  const { user } = useAuthStore();

  const hasTourForCurrentPage = hasOnboardingForCurrentRoute();

  const handleRestartTour = async () => {
    if (!user?.id) {
      return;
    }
    navStore.closeNavigation();
    // Small delay to allow the navigation sheet to close before starting tour
    setTimeout(async () => {
      await restartOnboardingForCurrentRoute(user.id);
    }, 300);
  };

  const watchfulOnlyFlag = useFeatureFlagEnabled(FeatureFlags.WatchfulOnly);
  const monitoringOverviewFlag = useFeatureFlagEnabled(FeatureFlags.MonitoringOverview);
  const quillAuditToolFlag = useFeatureFlagEnabled(FeatureFlags.QuillAuditTool);
  const ultraTopSecretFlag = useFeatureFlagEnabled(FeatureFlags.UltraTopSecret);
  const billingPageFlag = useFeatureFlagEnabled(FeatureFlags.BillingPage);
  const edgeLinkFlag = useFeatureFlagEnabled(FeatureFlags.EdgeLink);

  // Close navigation on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isNavigationOpen) {
        navStore.closeNavigation();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isNavigationOpen]);

  return (
    <Sheet open={isNavigationOpen} onOpenChange={(open) => !open && navStore.closeNavigation()}>
      <SheetPortal>
        <SheetOverlay className="bg-black/20 backdrop-blur-sm data-[state=open]:duration-75 data-[state=closed]:duration-300" />
        <SheetContent 
          side="left" 
          className="w-auto min-w-[24rem] border-r border-gray-800 bg-[#111] p-0 shadow-2xl data-[state=open]:duration-75 data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:translate-x-0 data-[state=closed]:duration-300 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:slide-out-to-left"
          onPointerDownOutside={() => navStore.closeNavigation()}
        >
        {/* Top Bar */}
        <div className="flex h-[3.5rem] items-center justify-start border-b">
          <button
            onClick={() => navStore.closeNavigation()}
            className="flex w-[3.5rem] items-center justify-center gap-2 border-r p-4 opacity-70 ring-offset-background transition-all duration-150 ease-out hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-5 w-5 text-lime" />
          </button>

          <a href="/" className="flex items-center gap-4">
            <img src="/logo.svg" alt="logo" className="ml-4 h-4" />{' '}
            {(() => {
              const hostname = window.location.hostname;
              if (hostname === 'beta.watchful.co.nz')
                return (
                  <div className="rounded-full border bg-green px-4 py-1 text-xs font-semibold text-white">
                    Beta
                  </div>
                );
              if (hostname.endsWith('.pages.dev'))
                return (
                  <div className="rounded-full border bg-lime px-4 py-1 text-xs font-semibold text-black">
                    Research Preview
                  </div>
                );
              if (hostname === 'localhost')
                return (
                  <div className="rounded-full border bg-gray-800 px-4 py-1 text-xs font-semibold">
                    Local
                  </div>
                );
              return null;
            })()}
          </a>
        </div>

        {/* Home and Help Buttons */}
        <div className="flex border-b">
          <Link
            to="/home"
            className="flex flex-1 items-center justify-center gap-2 border-r border-gray-700 p-3 transition-all duration-150 ease-out hover:bg-white/5"
            onClick={() => navStore.closeNavigation()}
          >
            <Home className="h-4 w-4 text-lime" />
            <span className="text-sm font-medium text-white/90">Home</span>
          </Link>
          <a
            href={HELP_CENTER_LINKS.helpCentreOverview}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-1 items-center justify-center gap-2 p-3 transition-all duration-150 ease-out hover:bg-white/5"
          >
            <HelpCircle className="h-4 w-4 text-lime" />
            <span className="text-sm font-medium text-white/90">Help</span>
          </a>
        </div>
        
        <div className="h-[calc(100vh-7rem)] min-w-[24rem] overflow-y-scroll text-white">
          <div className="hidden border-b max-md:block">
            <ShiftManager className="w-full" />
          </div>
          <div className="hidden border-b p-4 max-2xl:block">
            {hasRoleAccess(Roles.Monitoring) && <AlertSearch />}
          </div>

          <div className="flex flex-col gap-2">
            {Object.entries(navigationItems).map(([key, section]) => {
              // Skip sections that require the flag if it's not enabled
              if (
                section.flag &&
                !watchfulOnlyFlag &&
                !monitoringOverviewFlag &&
                !quillAuditToolFlag &&
                !ultraTopSecretFlag &&
                !billingPageFlag &&
                !edgeLinkFlag
              )
                return null;
              // Check if any items in the section are accessible
              const hasAccessibleItems = section.items.some(
                (item) => !item.route || hasAccessToRoute(item.route)
              );

              if (!hasAccessibleItems) return null;

              // Filter items based on flags and access rights
              const visibleItems = section.items.filter((item) => {
                // Skip items that require flags
                if (item.flag === FeatureFlags.WatchfulOnly && !watchfulOnlyFlag) return false;
                if (item.flag === FeatureFlags.MonitoringOverview && !monitoringOverviewFlag)
                  return false;
                if (item.flag === FeatureFlags.QuillAuditTool && !quillAuditToolFlag) return false;
                if (item.flag === FeatureFlags.UltraTopSecret && !ultraTopSecretFlag) return false;
                if (item.flag === FeatureFlags.BillingPage && !billingPageFlag) return false;
                if (item.flag === FeatureFlags.EdgeLink && !edgeLinkFlag) return false;

                // Skip items that the user doesn't have access to

                if (item.route && !hasAccessToRoute(item.route)) return false;

                return true;
              });

              // Don't render the section if there are no visible items
              if (visibleItems.length === 0) return null;

              return (
                <div key={key} className="relative border-t first:border-t-0">
                  <div className="left-4 bg-[#111] p-4 px-2 pb-2">
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-semibold text-white/60">{section.title}</h2>
                    </div>
                  </div>
                  <div className="flex flex-col group/section">
                    {visibleItems.map((item) => {
                      const isExternalLink = item.href.startsWith('http');
                      const isActive = !isExternalLink && location.pathname === item.href;

                      return (
                        <NavigationItemComponent
                          key={item.href}
                          item={item}
                          isActive={isActive}
                          onClick={() => navStore.closeNavigation()}
                          sectionKey={key}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="border-t p-4">Made by your friends at Watchful</p>
        </div>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  );
}
